import {
  MutationFunction,
  QueryKey,
  UseMutationOptions,
  UseMutationResult,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { apiCall } from "api/axios";
import { API_BASE_URLS } from "api/axios/apiConfig";
import { IApiErrorData, TApiMethod } from "api/axios/types";

interface MutationParams<T> {
  apiRoute: string;
  data: T;
  method: TApiMethod;
  module: keyof typeof API_BASE_URLS;
  useEncryption: boolean;
}

export const mutateData = async <T, R extends object>({
  apiRoute,
  data,
  method,
  module,
  useEncryption,
}: MutationParams<T>): Promise<R> => {
  const result = await apiCall<R | IApiErrorData>({
    apiRoute,
    module,
    config: {
      method,
      data,
    },
    useEncryption,
  });

  if ("status" in result && result.status === "failed") {
    throw result; // Ensures that React Query handles it
  }
  return result as R;
};

export const useMutateData = <T, R>(
  invalidateQueryKey?: QueryKey,
  options?: UseMutationOptions<R, unknown, MutationParams<T>, unknown>
): UseMutationResult<R, unknown, MutationParams<T>, unknown> => {
  const queryClient = useQueryClient();

  return useMutation<R, unknown, MutationParams<T>, unknown>({
    mutationFn: mutateData as MutationFunction<R, MutationParams<T>>,
    onSuccess: () => {
      if (invalidateQueryKey) {
        queryClient.invalidateQueries({ queryKey: invalidateQueryKey });
      }
    },
    ...options,
  });
};
